import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {InputNumber} from "primereact/inputnumber";
import {useTranslate} from "../../../system/useTranslate";
import PrimeDatePicker from "../../../system/components/prime-date-picker";
import {formatDate} from "../../../system/utils";
import {
    COMPLETION_UNITS,
    createDefaultRecurrence,
    END_MODES,
    MONTHLY_MODES,
    RECURRENCE_FREQUENCIES,
    recurrenceErrors,
} from "./task-recurrence-model";

export {RECURRENCE_FREQUENCIES, createDefaultRecurrence};
export const PERSIAN_WEEK_DAYS = [
    {value: 6, labelKey: "date-picker.weekday.saturday", shortLabelKey: "date-picker.weekday.saturday-short"},
    {value: 0, labelKey: "date-picker.weekday.sunday", shortLabelKey: "date-picker.weekday.sunday-short"},
    {value: 1, labelKey: "date-picker.weekday.monday", shortLabelKey: "date-picker.weekday.monday-short"},
    {value: 2, labelKey: "date-picker.weekday.tuesday", shortLabelKey: "date-picker.weekday.tuesday-short"},
    {value: 3, labelKey: "date-picker.weekday.wednesday", shortLabelKey: "date-picker.weekday.wednesday-short"},
    {value: 4, labelKey: "date-picker.weekday.thursday", shortLabelKey: "date-picker.weekday.thursday-short"},
    {value: 5, labelKey: "date-picker.weekday.friday", shortLabelKey: "date-picker.weekday.friday-short"},
];
const NTH_WEEKS = [1, 2, 3, 4, -1];

function TaskRecurrenceControl({value, onChange, startWeekDay = null}) {
    const translate = useTranslate();
    const [advancedOpen, setAdvancedOpen] = useState(value?.recurrenceMode === "completion");
    const recurring = value.frequency !== "none";
    const completion = recurring && value.recurrenceMode === "completion";

    const options = useMemo(() => RECURRENCE_FREQUENCIES.map((frequency) => ({
        value: frequency,
        label: translate(`date-picker.recurrence.${frequency}`),
    })), [translate]);
    const weekDayOptions = useMemo(() => PERSIAN_WEEK_DAYS.map((day) => ({
        value: day.value,
        label: translate(day.labelKey),
    })), [translate]);

    const update = useCallback((patch) => onChange({...value, ...patch}), [onChange, value]);

    const handleFrequencyChange = useCallback((event) => {
        const frequency = event.value;
        const weekDays = frequency === "weekly" && value.weekDays.length === 0 && startWeekDay !== null
            ? [startWeekDay]
            : value.weekDays;
        onChange({...value, frequency, weekDays});
    }, [onChange, startWeekDay, value]);

    const toggleWeekDay = useCallback((weekDay) => {
        const selected = value.weekDays.includes(weekDay);
        const weekDays = selected
            ? value.weekDays.filter((item) => item !== weekDay)
            : [...value.weekDays, weekDay];
        onChange({...value, weekDays});
    }, [onChange, value]);

    const weeklySelectionMissing = value.frequency === "weekly" && !completion && value.weekDays.length === 0;

    useEffect(() => {
        if (!weeklySelectionMissing || startWeekDay === null) return;
        onChange({...value, weekDays: [startWeekDay]});
    }, [onChange, startWeekDay, value, weeklySelectionMissing]);

    const errors = recurring ? recurrenceErrors(value).filter((key) => key !== "date-picker.recurrence.week-day-required") : [];
    const unitKey = completion ? null : value.frequency;

    return (
        <div className="zofino-recurrence flex flex-column gap-2 p-2 border-top-1 border-200 max-w-full" style={{minWidth: "16rem"}}>
            <label className="text-xs font-medium" htmlFor="task-recurrence-frequency">
                {translate("date-picker.recurrence.label")}
            </label>
            <Dropdown
                inputId="task-recurrence-frequency"
                value={value.frequency}
                options={options}
                onChange={handleFrequencyChange}
                className="w-full text-xs shadow-none"
                panelClassName="rtl text-xs zofino-recurrence-panel"
                aria-label={translate("date-picker.recurrence.label")}
            />

            {recurring && !completion && (
                <div className="flex align-items-center gap-2 text-xs">
                    <span>{translate("date-picker.recurrence.every")}</span>
                    <InputNumber
                        inputId="task-recurrence-interval"
                        value={value.interval}
                        min={1}
                        max={365}
                        onValueChange={(event) => update({interval: event.value || 1})}
                        inputClassName="w-4rem text-xs p-1 text-center"
                        aria-label={translate("date-picker.recurrence.every")}
                    />
                    <span>{translate(`date-picker.recurrence.unit.${unitKey}`)}</span>
                </div>
            )}

            {value.frequency === "weekly" && !completion && (
                <div className="flex flex-column gap-2">
                    <span className="text-xs text-600">{translate("date-picker.recurrence.week-days")}</span>
                    <div className="flex flex-wrap gap-1" role="group" aria-label={translate("date-picker.recurrence.week-days")}>
                        {PERSIAN_WEEK_DAYS.map((day) => {
                            const selected = value.weekDays.includes(day.value);
                            return (
                                <Button
                                    key={day.value}
                                    type="button"
                                    label={translate(day.shortLabelKey)}
                                    outlined={!selected}
                                    rounded
                                    className={`w-2rem h-2rem p-0 text-xs ${selected ? "zofino-recurrence-day-active" : ""}`}
                                    aria-label={translate(day.labelKey)}
                                    aria-pressed={selected}
                                    onClick={() => toggleWeekDay(day.value)}
                                />
                            );
                        })}
                    </div>
                    {weeklySelectionMissing && (
                        <small className="text-red-500" role="alert">
                            {translate("date-picker.recurrence.week-day-required")}
                        </small>
                    )}
                </div>
            )}

            {(value.frequency === "monthly" || value.frequency === "yearly") && !completion && (
                <div className="flex flex-column gap-2">
                    <Dropdown
                        value={value.monthlyMode}
                        options={MONTHLY_MODES.map((mode) => ({value: mode, label: translate(`date-picker.recurrence.monthly.${mode}`)}))}
                        onChange={(event) => update({monthlyMode: event.value})}
                        className="w-full text-xs shadow-none"
                        panelClassName="rtl text-xs zofino-recurrence-panel"
                        aria-label={translate("date-picker.recurrence.monthly.label")}
                    />
                    {value.monthlyMode === "nthWeekday" && (
                        <div className="flex gap-2">
                            <Dropdown
                                value={value.nthWeek}
                                options={NTH_WEEKS.map((nth) => ({value: nth, label: translate(`date-picker.recurrence.nth.${nth === -1 ? "last" : nth}`)}))}
                                onChange={(event) => update({nthWeek: event.value})}
                                className="flex-1 text-xs shadow-none"
                                panelClassName="rtl text-xs zofino-recurrence-panel"
                                aria-label={translate("date-picker.recurrence.nth.label")}
                            />
                            <Dropdown
                                value={value.nthWeekDay}
                                options={weekDayOptions}
                                onChange={(event) => update({nthWeekDay: event.value})}
                                placeholder={translate("date-picker.recurrence.weekday-placeholder")}
                                className="flex-1 text-xs shadow-none"
                                panelClassName="rtl text-xs zofino-recurrence-panel"
                                aria-label={translate("date-picker.recurrence.weekday-label")}
                            />
                        </div>
                    )}
                    {value.monthlyMode === "dayOfMonth" && (
                        <div className="flex flex-column gap-1">
                            <span className="text-2xs text-600">{translate("date-picker.recurrence.missing-day")}</span>
                            <Dropdown
                                value={value.missingDayPolicy}
                                options={["lastDay", "skip"].map((policy) => ({value: policy, label: translate(`date-picker.recurrence.missing-day.${policy}`)}))}
                                onChange={(event) => update({missingDayPolicy: event.value})}
                                className="w-full text-xs shadow-none"
                                panelClassName="rtl text-xs zofino-recurrence-panel"
                                aria-label={translate("date-picker.recurrence.missing-day")}
                            />
                        </div>
                    )}
                </div>
            )}

            {completion && (
                <div className="flex align-items-center gap-2 text-xs">
                    <span>{translate("date-picker.recurrence.after-completion")}</span>
                    <InputNumber
                        inputId="task-recurrence-interval"
                        value={value.interval}
                        min={1}
                        max={365}
                        onValueChange={(event) => update({interval: event.value || 1})}
                        inputClassName="w-4rem text-xs p-1 text-center"
                        aria-label={translate("date-picker.recurrence.after-completion")}
                    />
                    <Dropdown
                        value={value.completionUnit}
                        options={COMPLETION_UNITS.map((unit) => ({value: unit, label: translate(`date-picker.recurrence.completion-unit.${unit}`)}))}
                        onChange={(event) => update({completionUnit: event.value})}
                        className="flex-1 text-xs shadow-none"
                        panelClassName="rtl text-xs zofino-recurrence-panel"
                        aria-label={translate("date-picker.recurrence.completion-unit.label")}
                    />
                </div>
            )}

            {recurring && (
                <div className="flex flex-column gap-1">
                    <span className="text-xs text-600">{translate("date-picker.recurrence.end.label")}</span>
                    <div className="flex gap-2 align-items-center">
                        <Dropdown
                            value={value.endMode}
                            options={END_MODES.map((mode) => ({value: mode, label: translate(`date-picker.recurrence.end.${mode}`)}))}
                            onChange={(event) => update({endMode: event.value})}
                            className="flex-1 text-xs shadow-none"
                            panelClassName="rtl text-xs zofino-recurrence-panel"
                            aria-label={translate("date-picker.recurrence.end.label")}
                        />
                        {value.endMode === "count" && (
                            <div className="flex align-items-center gap-1 text-xs">
                                <InputNumber
                                    value={value.occurrences}
                                    min={1}
                                    max={1000}
                                    onValueChange={(event) => update({occurrences: event.value})}
                                    inputClassName="w-4rem text-xs p-1 text-center"
                                    aria-label={translate("date-picker.recurrence.end.count")}
                                />
                                <span>{translate("date-picker.recurrence.end.count-suffix")}</span>
                            </div>
                        )}
                    </div>
                    {value.endMode === "untilDate" && (
                        <PrimeDatePicker
                            inputClassName="w-full text-xs"
                            placeholder={translate("date-picker.recurrence.end-date-placeholder")}
                            value={value.endDate ? new Date(`${value.endDate}T12:00:00`).getTime() / 1000 : undefined}
                            onChange={(event) => update({endDate: formatDate(new Date(event.value * 1000), "YYYY-MM-DD")})}
                        />
                    )}
                </div>
            )}

            {recurring && (
                <div className="flex flex-column gap-1">
                    <button
                        type="button"
                        className="p-0 border-none bg-transparent cursor-pointer text-2xs text-primary text-right"
                        onClick={() => setAdvancedOpen((open) => !open)}
                        aria-expanded={advancedOpen}
                    >
                        {translate("date-picker.recurrence.advanced")}
                    </button>
                    {advancedOpen && (
                        <div className="flex flex-column gap-1 p-2 surface-50 border-round-lg" role="radiogroup" aria-label={translate("date-picker.recurrence.mode.label")}>
                            {["schedule", "completion"].map((mode) => (
                                <label key={mode} className="flex align-items-start gap-2 cursor-pointer text-xs">
                                    <input
                                        type="radio"
                                        name="task-recurrence-mode"
                                        checked={(value.recurrenceMode || "schedule") === mode}
                                        onChange={() => update({recurrenceMode: mode})}
                                    />
                                    <span className="flex flex-column">
                                        <span className="font-medium">{translate(`date-picker.recurrence.mode.${mode}`)}</span>
                                        <span className="text-2xs text-500">{translate(`date-picker.recurrence.mode.${mode}-hint`)}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {errors.map((key) => (
                <small key={key} className="text-red-500" role="alert">{translate(key)}</small>
            ))}
        </div>
    );
}

export default memo(TaskRecurrenceControl);
